import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, TrendingUp, Wallet, FileDown, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell } from "recharts";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { SHIFT_META, monthKey, monthLabel } from "@/lib/shifts";
import { currencySymbol } from "@/i18n/translations";
import { useSubscription } from "@/context/SubscriptionContext";

function Stat({ label, value, accent, sub }) {
  return (
    <div className="rounded-xl border border-white/10 bg-card p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-xl font-extrabold" style={accent ? { color: accent } : {}}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

export default function EarningsView() {
  const { t, lang } = useI18n();
  const { isPro, openPaywall } = useSubscription();
  const [cursor, setCursor] = useState(new Date());
  const [data, setData] = useState(null);
  const mk = monthKey(cursor);

  const load = useCallback(async () => {
    const res = await api.get("/earnings", { params: { month: mk } });
    setData(res.data);
  }, [mk]);

  useEffect(() => { load(); }, [load]);

  const changeMonth = (delta) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));
  const sym = data ? currencySymbol(data.currency) : "";
  const fmt = (n) => `${sym}${Number(n).toLocaleString(lang, { maximumFractionDigits: 0 })}`;

  const chartData = data
    ? ["morning", "afternoon", "night", "off"].map((s) => ({ name: t(s), value: data.shift_counts[s] || 0, key: s }))
    : [];

  const exportRows = () => [
    [t("totalPay"), `${sym}${data.total_pay}`],
    [t("regularPay"), `${sym}${data.base_pay}`],
    [t("overtimePay"), `${sym}${data.overtime_pay}`],
    [t("nightBonus"), `${sym}${data.night_bonus}`],
    [t("totalHours"), `${data.total_hours} h`],
    [t("overtimeHours"), `${data.overtime_hours} h`],
    [t("nightHours"), `${data.night_hours} h`],
    [t("hourlyRate"), `${sym}${data.hourly_rate}`],
  ];

  const exportCsv = () => {
    if (!isPro) { openPaywall(); return; }
    const header = `Vespera AI,${monthLabel(mk, lang)}\nMetric,Value\n`;
    const body = exportRows().map((r) => `"${r[0]}","${r[1]}"`).join("\n");
    const blob = new Blob([header + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vespera-earnings-${mk}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t("exported"));
  };

  const exportPdf = () => {
    if (!isPro) { openPaywall(); return; }
    const doc = new jsPDF();
    doc.setFontSize(20);
    doc.text("Vespera AI", 14, 20);
    doc.setFontSize(13);
    doc.setTextColor(120);
    doc.text(`${t("earnTitle")} — ${monthLabel(mk, lang)}`, 14, 29);
    doc.setTextColor(20);
    doc.setFontSize(11);
    let y = 44;
    exportRows().forEach((r) => {
      doc.text(String(r[0]), 14, y);
      doc.text(String(r[1]), 150, y, { align: "right" });
      doc.setDrawColor(230);
      doc.line(14, y + 3, 196, y + 3);
      y += 12;
    });
    doc.save(`vespera-earnings-${mk}.pdf`);
    toast.success(t("exported"));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold">{t("earnTitle")}</h2>
        {data && (
          <div className="flex gap-2">
            <button data-testid="export-csv-btn" onClick={exportCsv}
              className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-secondary/70 px-3 py-2 text-sm hover:border-white/25 transition-colors">
              <FileDown className="h-4 w-4" /> {t("exportCsv")}
            </button>
            <button data-testid="export-pdf-btn" onClick={exportPdf}
              className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-secondary/70 px-3 py-2 text-sm hover:border-white/25 transition-colors">
              <FileText className="h-4 w-4" /> {t("exportPdf")}
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between rounded-xl border border-white/10 bg-card px-3 py-2">
        <button data-testid="earn-prev-month" onClick={() => changeMonth(-1)} className="h-9 w-9 rounded-lg hover:bg-secondary flex items-center justify-center">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="font-semibold capitalize">{monthLabel(mk, lang)}</span>
        <button data-testid="earn-next-month" onClick={() => changeMonth(1)} className="h-9 w-9 rounded-lg hover:bg-secondary flex items-center justify-center">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {data && (
        <>
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-secondary/60 to-card p-6" data-testid="earnings-total">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Wallet className="h-4 w-4" />
              <span className="text-xs uppercase tracking-widest">{t("totalPay")}</span>
            </div>
            <p className="mt-2 font-display text-5xl font-extrabold">{fmt(data.total_pay)}</p>
            <p className="mt-1 text-sm text-muted-foreground">{data.total_hours} h · {sym}{data.hourly_rate}/h</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Stat label={t("regularPay")} value={fmt(data.base_pay)} sub={`${data.regular_hours} h`} />
            <Stat label={t("overtimePay")} value={fmt(data.overtime_pay)} accent="#38BDF8" sub={`${data.overtime_hours} ${t("overtimeHours")}`} />
            <Stat label={t("nightBonus")} value={fmt(data.night_bonus)} accent="#818CF8" sub={`${data.night_hours} ${t("nightHours")}`} />
            <Stat label={t("totalHours")} value={`${data.total_hours} h`} />
          </div>

          <div className="rounded-xl border border-white/10 bg-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("shiftMix")} ({t("days")})</p>
            </div>
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={chartData} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fill: "#A1A1AA", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {chartData.map((d) => (
                    <Cell key={d.key} fill={SHIFT_META[d.key].color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}
